/**
 * Tab attention helper — blinks the document title with a bell glyph when
 * a notification arrives while the tab is not focused. Keeps the favicon
 * untouched. Singleton state: calling `notifyTabAttention` multiple times
 * before the user comes back is idempotent. Attention is cleared as soon
 * as the window regains focus or visibility.
 */

const NOTIF_TITLE = "\u{1F514} Nouveau message";
const BLINK_INTERVAL_MS = 1000;

type AttentionState = {
  originalTitle: string | null
  intervalId: number | null
  listenersBound: boolean
};

const state: AttentionState = {
  originalTitle: null,
  intervalId: null,
  listenersBound: false,
};

function bindListenersOnce() {
  if (state.listenersBound) return;
  state.listenersBound = true;
  const restore = () => stopTabAttention();
  window.addEventListener("focus", restore);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") restore();
  });
}

export function notifyTabAttention() {
  if (typeof document === "undefined") return;
  // If the user is already looking at the tab there's nothing to alert about.
  if (document.hasFocus() && document.visibilityState === "visible") return;
  bindListenersOnce();
  if (state.intervalId !== null) return;

  if (state.originalTitle === null) state.originalTitle = document.title;

  let toggled = false;
  const tick = () => {
    document.title = toggled ? (state.originalTitle ?? "") : NOTIF_TITLE;
    toggled = !toggled;
  };
  tick();
  state.intervalId = window.setInterval(tick, BLINK_INTERVAL_MS);
}

export function stopTabAttention() {
  if (state.intervalId !== null) {
    window.clearInterval(state.intervalId);
    state.intervalId = null;
  }
  if (state.originalTitle !== null) {
    document.title = state.originalTitle;
  }
}
